define((require) => {
    "use strict";

    const ImprovedView                 = require("editor/zbone/ImprovedView");
    const ContentQuery                 = require("data/ContentQuery");
    const EditBehavior                 = require("ui/behaviors/EditBehavior");
    const ImageTagCompositeView        = require("ui/assets/image/tags/ImageTagCompositeView");
    const InventoryTagCompositeView    = require("ui/assets/image/inventory/InventoryTagCompositeView");
    const ImageDetailsTrackingBehavior = require("ui/imageLibrary/ImageDetailsTrackingBehavior");
    const ImageExperienceCompositeView = require("ui/imageLibrary/ImageExperienceCompositeView.js");
    const template                     = require("ui/imageLibrary/ImageDetailsView.tpl");

    /**
     * Represents the image quick view contents, containing detailed information about a specific image.
     *
     * @cfg {ImageModel} model the image model.
     *
     * @see DetailedImageLibraryCollectionView
     *
     * @class ImageDetailsView
     */
    return ImprovedView.extend("ImageDetailsView", {

        template,

        templateHelpers() {
            const {
                imageTaggingEnabled,
                inventoryEnabled,
                spectatorMode,
                experienceCount,
                partialViewOptions
            } = this.options;

            return {
                editable: !spectatorMode,
                showTags: imageTaggingEnabled,
                showInventory: inventoryEnabled,
                showExperiences: partialViewOptions && partialViewOptions.showExperiences,
                experienceCount
            };
        },

        className: "image-details-view",

        ui: {
            "addTagInput": ".add-tag-input",
            "toggleExperiencesButton": ".toggle-experiences-button",
            "experiencesAccordion": ".image-experiences-collection-region"
        },

        regions: {
            "tagsCollectionRegion": ".tags-collection-region",
            "inventoryTagsCollectionRegion": ".inventory-collection-region",
            "experiencesRegion": ".image-experiences-collection-region"
        },

        events: {
            "click @ui.toggleExperiencesButton": "_toggleExperiences"
        },

        behaviors: {
            nameEditBehavior: {
                behaviorClass: EditBehavior,
                container: ".image-name",
                editFields: [{
                    input: ".edit-name",
                    model: "name",
                    save() {
                        this.model.saveLater();
                    }
                }]
            },
            altTextEditBehavior: {
                behaviorClass: EditBehavior,
                container: ".js-alt-text-editable-container",
                editFields: [{
                    input: ".js-alt-text",
                    icon: ".js-alt-text-icon",
                    model: "altText",
                    save() {
                        this.model.saveLater();
                    }
                }]
            },
            imageEditingTracking: {
                behaviorClass: ImageDetailsTrackingBehavior,
                tracking: {
                    name: "EditImageNameInputFieldQuickView",
                    altText: "EditImageAltTextInputFieldQuickView"
                }
            }
        },

        bindings: {
            ".edit-name": {
                observe: "name",
                updateMethod: "html",
                escape: true
            },
            ".js-alt-text": {
                observe: "altText",
                updateMethod: "html",
                escape: true
            }
        },

        modelEvents: {
            "change:tags[*]": "_changeTagsHandler"
        },

        _tagsCollection: null,
        _inventoryTagsCollection: null,
        _imageTagsCollection: null,
        _experienceCollection: null,
        _togglingExperiences: false, // True if toggling is in progress

        _changeTagsHandler(changedTagModel) {
            const value = changedTagModel.get("name");
            const nameExists = this._tagsCollection.models.find((tagModel) => {
                if (tagModel === changedTagModel) {
                    return false;
                }
                return tagModel.get("name").toUpperCase() === value.toUpperCase();
            });

            if (nameExists) {
                changedTagModel.destroy();
            }

            this.model.saveLater();
        },

        initialize() {
            this._tagsCollection = this.model.get("tags");
            this._imageTagsCollection = this.model.get("imageTags");
            this._inventoryTagsCollection = this.model.get("inventoryTags");
            this._experienceCollection = this.options.experienceCollection;

            this.listenTo(this._imageTagsCollection, "add remove", () => {
                this._imageTagsCollection.sort();
                this.model.saveLater();
            });

            this.listenTo(this._inventoryTagsCollection, "add remove", () => {
                this._inventoryTagsCollection.sort();
                this.model.saveLater();
            });
        },

        onShow() {
            const imageTagCompositeView = new ImageTagCompositeView({
                collection: this._imageTagsCollection,
                tagCollection: this._tagsCollection,
                editable: !this.options.spectatorMode,
                trackingOptions: {
                    triggerOrigin: "ImageQuickView",
                    imageModel: this.model
                }
            });

            this.tagsCollectionRegion.show(imageTagCompositeView);

            /**
             * You have to pss both the filtered and unfiltered tags because we need to save to both
             * */
            const inventoryTagCompositeView = new InventoryTagCompositeView({
                collection: this._inventoryTagsCollection,
                tagCollection: this._tagsCollection,
                editable: !this.options.spectatorMode,
                trackingOptions: {
                    triggerOrigin: "ImageQuickView",
                    imageModel: this.model
                }
            });

            this.inventoryTagsCollectionRegion.show(inventoryTagCompositeView);

            const imageExperienceCompositeView = new ImageExperienceCompositeView({
                collection: this._experienceCollection,
                horizontalBuffer: 220,
                scrollTarget: this.ui.experiencesAccordion,
                itemEvents: {
                    "clicked:experience:item": this._clickedExperienceItem.bind(this)
                }
            });

            this.experiencesRegion.show(imageExperienceCompositeView);

            // Preload first chunk of data
            this._loadExperiencesChunk();

            // Follow toggling animation progress and save the state
            this.ui.experiencesAccordion.on("show.bs.collapse hide.bs.collapse", () => {
                this._togglingExperiences = true;
            });
            this.ui.experiencesAccordion.on("shown.bs.collapse hidden.bs.collapse", () => {
                this._togglingExperiences = false;
            });
        },

        _clickedExperienceItem() {
            this.trigger("edit:experience");
        },

        _loadExperiencesChunk() {
            this._experienceCollection.query(ContentQuery.sort().field("createdAt"));
        },

        _toggleExperiences() {
            // Do not allow toggling while toggling is in progress
            if (this._togglingExperiences) {
                return;
            }

            const isOpen = this.ui.experiencesAccordion.hasClass("in");
            this.ui.toggleExperiencesButton.toggleClass("open", !isOpen);

            this.trigger("toggled:experiences", this.model, isOpen);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/imageLibrary/ImageDetailsView.js